/**
 * Plain text building blocks of the compact log entries in the quick view of the global search.
 * Pure functions without Vue / store / DOM imports (checked by tests/search/searchLogText.check.ts).
 */

const OBJECT_ID = /^[0-9a-f]{24}$/i;

/** True if the value is nothing but a (24 hex digit) ObjectId, e.g. an unpopulated reference */
export function isIdOnly(value: unknown): boolean {
    return typeof value === 'string' && OBJECT_ID.test(value.trim());
}

/** Trimmed text of a value; '' for non strings and for bare ObjectIds (never shown as content) */
export function readableText(value: unknown): string {
    if (typeof value !== 'string') {
        return '';
    }
    const text = value.trim();
    return OBJECT_ID.test(text) ? '' : text;
}

/** Name of a populated reference ({name} / {title}) or a plain name; '' for unpopulated ids */
export function referenceName(value: unknown): string {
    if (value && typeof value === 'object') {
        const ref = value as { name?: unknown; title?: unknown };
        return readableText(ref.name) || readableText(ref.title);
    }
    return readableText(value);
}

export type CourseLogPayload = {
    customId?: unknown;
    subject?: unknown;
    topic?: unknown;
    subTopic?: unknown;
};

/**
 * Summary of a course log entry: "Gitarrenunterricht · K25.803 · Thema: Die Eulenbande (Übung 5) · anwesend".
 * The subject only if it is populated; `stateText` is the presence label (e.g. "Anwesend").
 */
export function courseLogSummary(payload: CourseLogPayload, stateText = ''): string {
    const topic = readableText(payload.topic);
    const subTopics = Array.isArray(payload.subTopic) ? payload.subTopic.map(readableText).filter((el) => el) : [];
    let topicPart = '';
    if (topic) {
        topicPart = `Thema: ${topic}${subTopics.length ? ` (${subTopics.join(', ')})` : ''}`;
    } else if (subTopics.length) {
        topicPart = `Thema: ${subTopics.join(', ')}`;
    }
    return [
        referenceName(payload.subject),
        readableText(payload.customId),
        topicPart,
        readableText(stateText).toLowerCase(),
    ].filter((el) => el).join(' · ');
}

/**
 * Text of a generic entry: the message, otherwise "subject – preview" (mail subject, call summary).
 * Values that are only an ObjectId are skipped.
 */
export function genericLogText(message: unknown, subject: unknown, preview: unknown): string {
    return readableText(message) || [readableText(subject), readableText(preview)].filter((el) => el).join(' – ');
}

/** Course log: its message if there is a readable one, otherwise the summary built from the payload */
export function courseLogText(payload: CourseLogPayload, message: unknown, stateText = ''): string {
    return readableText(message) || courseLogSummary(payload, stateText);
}

/** Shortens a text to `max` characters with an ellipsis */
export function shorten(text: string, max: number): string {
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
